import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal, MaskLine, easeOut } from "@/components/Reveal";
import FinishLibrary from "@/components/FinishLibrary";
import QuoteForm from "@/components/QuoteForm";
import { STATS, TRUST, SERVICES, PROCESS, GALLERY, TESTIMONIALS, CONTACT, IMG } from "@/lib/content";

export default function Home() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <>
      {/* ---------------- HERO ---------------- */}
      <section ref={heroRef} data-testid="hero" className="relative min-h-screen flex items-end overflow-hidden pt-32 pb-20">
        <motion.div className="absolute inset-0 z-0" style={{ y: imgY, scale: imgScale }}>
          <img src={IMG.hero} alt="Metallic epoxy floor" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(12,11,9,0.72) 0%, rgba(12,11,9,0.55) 40%, rgba(12,11,9,0.92) 100%)" }} />
        </motion.div>

        <div className="pour-field absolute inset-0 z-[1]">
          <span className="blob" style={{ width: 620, height: 620, top: -180, right: -160, opacity: 0.5, background: "radial-gradient(circle at 35% 30%, var(--brass-bright), var(--copper) 55%, transparent 75%)" }} />
          <span className="blob" style={{ width: 420, height: 420, bottom: -140, left: -100, opacity: 0.3, animationDelay: "-6s", background: "radial-gradient(circle at 60% 60%, var(--copper), transparent 70%)" }} />
        </div>

        <div className="wrap relative z-[2] w-full">
          <div className="grid lg:grid-cols-[1.1fr_0.7fr] gap-12 lg:items-end">
            <div>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="flex items-center gap-3 mb-7">
                <span className="inline-block w-9 h-px" style={{ background: "var(--brass-bright)" }} />
                <span className="font-mono-lbl text-[11.5px]" style={{ color: "var(--brass-bright)" }}>Epoxy Flooring &amp; Surface Artistry</span>
              </motion.div>

              <h1 className="font-serif" style={{ fontSize: "clamp(44px, 6.6vw, 88px)", lineHeight: 0.98, color: "var(--champagne)" }}>
                <MaskLine delay={0.15}>Floors poured</MaskLine>
                <MaskLine delay={0.32} className="mt-1">
                  <span>like </span>
                  <span className="liquid-text">liquid Glass.</span>
                </MaskLine>
              </h1>

              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8, ease: easeOut }}
                className="mt-7 max-w-[490px] text-[16px] sm:text-[17px] leading-relaxed font-semibold" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--brass-bright)" }}>
                LuxeBond transforms garages, homes, countertops, and industrial floors into seamless,
                high-gloss surfaces engineered to outlast the ordinary — and look extraordinary doing it.
              </motion.p>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8, ease: easeOut }}
                className="flex flex-wrap gap-4 mt-10">
                <button data-testid="hero-quote-btn" onClick={() => jump("quote")} className="btn btn-primary">
                  Request a Free Quote <ArrowRight size={16} />
                </button>
                <a href={CONTACT.visualizer} target="_blank" rel="noopener noreferrer" data-testid="hero-visualizer-btn" className="btn btn-ghost">
                  Floor Visualizer <ArrowUpRight size={16} />
                </a>
              </motion.div>
            </div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.8 }}
              className="lg:border-l lg:pl-9 flex lg:block gap-8 flex-wrap" style={{ borderColor: "var(--line)" }}>
              {STATS.map((s) => (
                <div key={s.label} className="mb-7">
                  <div className="font-serif text-[38px] leading-none" style={{ color: "var(--brass-bright)" }}>
                    {s.num} {s.suffix && <span className="text-[20px]">{s.suffix}</span>}
                  </div>
                  <div className="text-[12px] mt-2 tracking-[0.04em]" style={{ color: "var(--stone)" }}>{s.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        <motion.div style={{ opacity: fade }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-[2] flex flex-col items-center gap-2">
          <span className="font-mono-lbl text-[10px]" style={{ color: "var(--stone)" }}>Scroll</span>
          <span className="w-px h-9" style={{ background: "linear-gradient(var(--brass-bright), transparent)" }} />
        </motion.div>
      </section>

      {/* ---------------- TRUST MARQUEE ---------------- */}
      <div data-testid="trust-strip" style={{ borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)", background: "var(--ink)" }} className="py-6">
        <Marquee gradient gradientColor="#0c0b09" gradientWidth={120} speed={40} autoFill>
          {TRUST.map((t) => (
            <div key={t} className="flex items-center gap-4 mx-9">
              <span className="rounded-full" style={{ width: 5, height: 5, background: "var(--brass)" }} />
              <span className="font-mono-lbl text-[12px]" style={{ color: "var(--stone)" }}>{t}</span>
            </div>
          ))}
        </Marquee>
      </div>

      {/* ---------------- SERVICES ---------------- */}
      <section id="services" data-testid="services-section" className="py-[120px]" style={{ background: "var(--ink)" }}>
        <div className="wrap">
          <Reveal>
            <SectionHead eyebrow="What We Pour" title="One material, engineered for every space you own."
              body="From a single garage bay to a full production floor, every LuxeBond system is custom-mixed and hand-troweled on site — never a stock kit, never a shortcut." />
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4" style={{ gap: 1, background: "var(--line)", border: "1px solid var(--line)" }}>
            {SERVICES.map((s, i) => (
              <Reveal key={s.idx} delay={i * 0.08}>
                <div data-testid={`service-card-${s.idx}`} className="group relative h-full min-h-[280px] p-8 flex flex-col justify-between transition-colors" style={{ background: "var(--ink)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--ink-2)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "var(--ink)")}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono-lbl text-[11px]" style={{ color: "var(--stone-dim)" }}>{s.idx} / {s.tag}</span>
                  </div>
                  <div>
                    <h3 className="font-serif text-[22px]" style={{ color: "var(--champagne)" }}>{s.title}</h3>
                    <p className="text-[14px] leading-relaxed mt-3" style={{ color: "var(--stone)" }}>{s.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- FINISH LIBRARY ---------------- */}
      <section data-testid="finishes-section" className="py-[120px]" style={{ background: "var(--ink-2)" }}>
        <div className="wrap">
          <Reveal>
            <SectionHead eyebrow="The Finish Library" title="Four systems. Infinite floors."
              body="Every LuxeBond floor starts from one of four base systems, then gets customized to your palette, sheen, and traffic needs. Select a system to see it up close." />
          </Reveal>
          <Reveal delay={0.1}><FinishLibrary /></Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 flex justify-end">
              <button onClick={() => navigate("/finishes")} className="btn btn-ghost" data-testid="finishes-page-link">
                Explore all finishes <ArrowRight size={16} />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- PROCESS ---------------- */}
      <section data-testid="process-section" className="py-[120px]" style={{ background: "var(--ink)" }}>
        <div className="wrap">
          <Reveal>
            <SectionHead eyebrow="How A Floor Is Made" title="Four stages, one continuous pour."
              body="Epoxy only performs if the sequence is right. This is the exact order our crews follow on every job — residential or industrial." />
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-9">
            {PROCESS.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.08}>
                <div className="relative pt-7">
                  <span className="absolute top-0 left-0 h-[2px] w-full" style={{ background: "linear-gradient(90deg,var(--brass-bright),transparent)" }} />
                  <span className="font-serif text-[52px] leading-none" style={{ color: "var(--stone-dim)" }}>{p.num}</span>
                  <h4 className="font-serif text-[20px] mt-4" style={{ color: "var(--champagne)" }}>{p.title}</h4>
                  <p className="text-[14px] leading-relaxed mt-3" style={{ color: "var(--stone)" }}>{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- GALLERY ---------------- */}
      <section id="gallery" data-testid="gallery-section" className="py-[120px]" style={{ background: "var(--ink-2)" }}>
        <div className="wrap">
          <Reveal>
            <SectionHead eyebrow="Recent Pours" title="A floor is the largest surface in any room."
              body="We treat it that way. A sample of finishes from recent residential and industrial installs." />
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 auto-rows-[220px] gap-3.5">
            {GALLERY.map((g, i) => (
              <Reveal key={g.label} delay={(i % 3) * 0.08} className={g.span ? "sm:col-span-2" : ""}>
                <GalleryTile {...g} />
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-8 flex justify-end">
              <button onClick={() => navigate("/gallery")} className="btn btn-ghost" data-testid="gallery-page-link">
                View full gallery <ArrowRight size={16} />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- TESTIMONIALS ---------------- */}
      <section data-testid="testimonials-section" className="py-[120px]" style={{ background: "var(--ink)" }}>
        <div className="wrap">
          <Reveal>
            <SectionHead eyebrow="Client Word" title="What the floor says after we leave." />
          </Reveal>
          <div className="grid lg:grid-cols-3" style={{ gap: 1, background: "var(--line)", border: "1px solid var(--line)" }}>
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.who} delay={i * 0.08}>
                <div className="p-9 h-full" style={{ background: "var(--ink)" }}>
                  <div className="text-[13px] tracking-[3px] mb-5" style={{ color: "var(--brass-bright)" }}>★★★★★</div>
                  <p className="font-serif italic text-[16px] leading-relaxed" style={{ color: "var(--champagne)" }}>"{t.quote}"</p>
                  <div className="mt-6 text-[12px] tracking-[0.04em]" style={{ color: "var(--stone)" }}>
                    <b className="not-italic" style={{ color: "var(--champagne)" }}>{t.who}</b> — {t.role}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- QUOTE FORM ---------------- */}
      <QuoteForm />
    </>
  );
}

export function SectionHead({ eyebrow, title, body }) {
  return (
    <div className="mb-16 max-w-[640px]">
      <div className="flex items-center gap-3 mb-4">
        <span className="inline-block w-9 h-px" style={{ background: "var(--brass-bright)" }} />
        <span className="font-mono-lbl text-[11px]" style={{ color: "var(--brass-bright)" }}>{eyebrow}</span>
      </div>
      <h2 className="font-serif text-3xl sm:text-5xl leading-[1.05]" style={{ color: "var(--champagne)" }}>{title}</h2>
      {body && <p className="mt-5 text-[16px] leading-relaxed" style={{ color: "var(--stone)" }}>{body}</p>}
    </div>
  );
}

function GalleryTile({ label, image }) {
  return (
    <div data-testid="gallery-tile" className="group relative h-full overflow-hidden flex items-end p-5" style={{ border: "1px solid var(--line)" }}>
      <img src={image} alt={label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(0deg, rgba(0,0,0,0.7), transparent 55%)" }} />
      <span className="absolute top-[-50%] left-[-30%] w-2/5 h-[200%] z-[1] rotate-[20deg] -translate-x-[40%] group-hover:translate-x-[160%] transition-transform duration-[1100ms]"
        style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.14), transparent)" }} />
      <span className="relative z-[2] font-mono-lbl text-[11.5px]" style={{ color: "var(--champagne)" }}>{label}</span>
    </div>
  );
}
